import { useCallback, useEffect, useState } from 'react'

import { UpdatedQueueStatus, UploadResponse, UploadStatus } from '@shared/models'

import { useRouting, useSQS } from '@/hooks'

import { Alert, Api, Confirmation } from '@/modules'
import { UploadProgress } from '@/modules/api/useCommand'

import { Button, Flex } from '@/components'
import { Paginator, SkeletonLoader } from '@/components/derived'
import { Layout } from '@/components/layout'

import { Tokens } from '@/assets/tokens'
import { PageTitle } from '@/components/derived/PageTitle'
import { Routes } from '@/configs'
import { getAwsCredentials, getAwsRegion } from '@/configs/aws.config'
import { Env } from '@/configs/env'
import { DeleteFive, UploadLogs } from '@icon-park/react'
import { LocalStorageHandler } from '../../utils/LocalStorageHandler'
import { CompanyDetailsContextProvider, useCompanyContext } from '../CompanyDetails/CompanyDetails.context'
import { Card, UploadItem } from './components/Card/Card.component'
import { Uploader } from './components/Uploader/Uploader.component'
import { UploadsContextProvider, useUploadsContext } from './Uploads.context'
import { CancelButton, CardsContainer, PaginationContainer, SelectButton } from './Uploads.styles'

type QueueUploadStatus = {
  uploadID: string
  status: UpdatedQueueStatus
}

const PAGE_SIZE = 15
const MAX_NUMBER_OF_TEN_MESSAGES = 10
const POLLING_INTERVAL_OF_FIVE_SECONDS = 5000
const UPLOAD_RESPONSES_KEY = 'UPLOAD_RESPONSES_KEY'
const localStorageHandler = new LocalStorageHandler<UploadResponse>({
  key: UPLOAD_RESPONSES_KEY,
  existsFn: (itemA, itemB) => itemA && itemA?.id === itemB?.id,
})

export function Uploads() {
  return (
    <CompanyDetailsContextProvider>
      <UploadsContextProvider>
        <UploadsComponent />
      </UploadsContextProvider>
    </CompanyDetailsContextProvider>
  )
}

export function UploadsComponent() {
  const { companyID, navigate, queryParams } = useRouting()
  const { isLoadingCompany, company } = useCompanyContext()
  const currentPage = Number(queryParams.pagina ?? '1')

  const {
    uploads,
    isLoadingUploads,
    loadUploads,
    selectedUploadIds,
    toggleUploadSelection,
    clearSelection,
    batchDeleteUploads,
    isDeletingBatch,
    selectAllVisible,
    toggleSelectAllVisible,
  } = useUploadsContext()

  const [currentFile, setCurrentFile] = useState<File | undefined>()
  const [currentUploadStatus, setCurrentUploadStatus] = useState<UploadStatus>(UploadStatus.UPLOADING)
  const [uploadedItems, setUploadedItems] = useState<UploadItem[]>([])
  const [isSelectionModeActive, setSelectionModeActive] = useState(false)

  const { sendAlert } = Alert.Hook()

  const { open: openDeleteConfirmation } = Confirmation.Hook({
    icon: DeleteFive,
    text: `Esta ação irá excluir permanentemente os ${selectedUploadIds?.size ?? 0} uploads selecionados, assim como seus logs associados. Tem certeza que deseja apagar os uploads?`,
    confirmText: 'Sim! Apagar uploads',
  })

  const { run: uploadSpreadsheet, data: uploadResponse } = Api.Command<UploadResponse>(
    Api.Methods.POST,
    `companies/${companyID}/spreadsheets`,
  )

  const awsCredentials = getAwsCredentials()
  const { fetchMessages, deleteMessage, messages } = useSQS<QueueUploadStatus>({
    region: getAwsRegion(),
    ...(awsCredentials && {
      accessKeyId: awsCredentials.accessKeyId,
      secretAccessKey: awsCredentials.secretAccessKey,
    }),
    queueUrl: process.env[Env.REACT_APP_SPREADSHEET_UPDATED_QUEUE] || '',
    maxNumberOfMessages: MAX_NUMBER_OF_TEN_MESSAGES,
  })

  function handleUpload(file: File, companyClipperID: string): void {
    if (!file) return

    const fileName = file.name.toLowerCase()
    if (!fileName.endsWith('.xlsx')) {
      sendAlert(
        {
          type: 'error',
          title: 'Arquivo inválido!',
          message: 'Apenas arquivos .xlsx são permitidos!',
        },
        {
          ttl: 3000,
        },
      )
      return
    }

    setCurrentFile(file)

    const formData = new FormData()
    formData.append('file', file)
    formData.append('companyClipperID', companyClipperID)

    uploadSpreadsheet<FormData>(
      {
        input: formData,
        onSuccess: (): void => {
          setCurrentFile(undefined)
          loadUploads({
            size: PAGE_SIZE,
            page: currentPage - 1,
          })
        },
        onFail: (): void => {
          setCurrentFile(undefined)
          sendAlert(
            {
              type: 'error',
              title: 'Erro no upload!',
              message: 'Ocorreu um erro ao fazer upload da planilha!',
            },
            {
              ttl: 2000,
            },
          )
          removeTemporaryUploadedItem()
        },
      },
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: handleUploadProgress,
      },
    )
  }

  const handleBatchDelete = useCallback(() => {
    openDeleteConfirmation(batchDeleteUploads)
  }, [openDeleteConfirmation, batchDeleteUploads])

  const handleCancelSelection = () => {
    setSelectionModeActive(false)
    clearSelection()
  }

  function removeTemporaryUploadedItem() {
    if (!uploadedItems?.length) {
      return
    }

    const newUploadedItems = uploadedItems.filter((item) => !item?.id?.includes('TEMP'))
    setUploadedItems(newUploadedItems)
  }

  function handleUploadProgress({ loaded, total }: UploadProgress): void {
    const percent = Math.round((loaded * 100) / (total ?? 0))
    setCurrentUploadStatus(percent < 100 ? UploadStatus.UPLOADING : UploadStatus.UPLOADED)
  }

  function createTemporaryUploadItem() {
    if (!currentFile) {
      return
    }

    const tempUploadedItem: UploadItem = {
      id: 'TEMP-' + (Math.random() * 10000000000).toString(),
      originalFileName: currentFile?.name,
      percentage: convertStatusToPercentage(currentUploadStatus),
      status: currentUploadStatus,
    }

    if (!uploadedItems?.length) {
      setUploadedItems([tempUploadedItem])
    }

    const newUploadedItems = [...uploadedItems]
    newUploadedItems.unshift(tempUploadedItem)

    setUploadedItems(newUploadedItems)
  }

  function syncStatusOfUploadedItems(items: UploadItem[]) {
    if (!items?.length || !messages?.length) {
      return
    }

    items.forEach((uploadedItem) => {
      const filtererMessages = filterMessagesById(uploadedItem?.id)

      if (filtererMessages?.length) {
        filtererMessages.forEach((message) => syncUploadedItemStatus(uploadedItem, message?.body))
      }
    })
  }

  function filterMessagesById(id: string | undefined) {
    if (!id) {
      return
    }

    return messages?.filter((message) => message?.body?.uploadID === id)
  }

  function syncUploadedItemStatus(uploadItem: UploadItem, queueUploadStatus: QueueUploadStatus | undefined): void {
    if (!uploadItem || !queueUploadStatus) {
      return
    }

    const newUploadStatus = convertToUploadStatus(queueUploadStatus.status)
    const oldPercentage = convertStatusToPercentage(uploadItem.status)
    const newPercentage = convertStatusToPercentage(newUploadStatus)

    if (newPercentage > oldPercentage) {
      uploadItem.percentage = newPercentage
      uploadItem.status = newUploadStatus
    }
  }

  function convertToUploadStatus(status: UpdatedQueueStatus): UploadStatus {
    if (!status) {
      return UploadStatus.UPLOADED
    }

    const found = Object.entries(UploadStatus)
      .map(([key, value]) => ({
        key,
        value,
      }))
      .find((e) => e?.key?.toString() === String(status))

    return found?.value ? found.value : UploadStatus.UPLOADED
  }

  function convertStatusToPercentage(status: UploadStatus): number {
    if (!status) {
      return 0
    }

    switch (status) {
      case UploadStatus.UPLOADING:
        return 10
      case UploadStatus.UPLOADED:
        return 25
      case UploadStatus.STARTED:
        return 50
      default:
        return 100
    }
  }

  function handleFetchMessages() {
    const uploadPending = uploadedItems?.find((e) =>
      [UploadStatus.STARTED, UploadStatus.UPLOADING, UploadStatus.UPLOADED].includes(e.status),
    )

    if (uploadPending) {
      fetchMessages()
    } else {
      localStorageHandler.clearAll()
    }
  }

  function deleteMessagesOfUploadItens() {
    const uploadResponses = localStorageHandler.getAll()

    uploadResponses?.forEach((item) => {
      if (item?.id) {
        const filtererMessages = filterMessagesById(item?.id)

        if (filtererMessages?.length) {
          filtererMessages.forEach((filtererMessage) => deleteMessage(filtererMessage))
        }
      }
    })
  }

  useEffect(() => {
    if (!uploadResponse?.id) {
      return
    }

    localStorageHandler.save(uploadResponse)
  }, [uploadResponse])

  useEffect(() => {
    createTemporaryUploadItem()
  }, [currentFile])

  useEffect(() => {
    if (!uploadedItems?.length) {
      return
    }

    const newUploadedItems = [...uploadedItems]
    syncStatusOfUploadedItems(newUploadedItems)
    deleteMessagesOfUploadItens()
    deleteMessagesOfUploadItens()
    setUploadedItems(newUploadedItems)
  }, [messages])

  useEffect(() => {
    if (!uploadedItems?.length) {
      return
    }

    const poolingInterval = setInterval(handleFetchMessages, POLLING_INTERVAL_OF_FIVE_SECONDS)
    return () => clearInterval(poolingInterval)
  }, [uploadedItems])

  useEffect(() => {
    const newUploadedItems = uploads?.elements?.map((upload: UploadResponse): UploadItem => {
      return {
        id: upload.id,
        originalFileName: upload?.originalFileName,
        percentage: convertStatusToPercentage(upload.status),
        status: upload.status,
        metadata: upload.metadata,
        createdAt: upload.createdAt,
        taskId: upload.taskId,
        executionTime: upload.executionTime,
      }
    })

    setUploadedItems(newUploadedItems ? newUploadedItems : [])
  }, [uploads])

  useEffect(() => {
    loadUploads({
      size: PAGE_SIZE,
      page: currentPage,
    })
  }, [currentPage])

  if (company && !company.healthCheck!.isHealthy) {
    sendAlert({
      type: 'error',
      title: 'Erro no manual',
      message: 'Empresa está com manual incompleto: Favor revisar o manual!',
    })
    navigate(Routes.CompanyDetails.path, companyID!)
  }

  const SelectionHeader = () => (
    <Flex $type="row" $justify="flex-end" $align="center" style={{ width: '100%' }}>
      {!isSelectionModeActive ?
        <Flex $type="row" $align="center">
          <SelectButton $variant="link" onClick={() => setSelectionModeActive(true)}>
            <Button.Text value="Selecionar" />
          </SelectButton>
          <span style={{ color: Tokens.ColorUIPrimary }}>|</span>
          <SelectButton
            $variant="link"
            onClick={() => {
              setSelectionModeActive(true)
              if (!selectAllVisible) {
                toggleSelectAllVisible()
              }
            }}>
            <Button.Text value="Selecionar todos" />
          </SelectButton>
        </Flex>
      : <Flex $type="row" $justify="flex-end" $align="center" style={{ width: '100%' }}>
          <Flex $type="row" $align="center" $gap={4}>
            <Button.Root
              $variant="link"
              onClick={handleBatchDelete}
              disabled={!selectedUploadIds || selectedUploadIds.size === 0}
              $loading={isDeletingBatch}
              title={
                !selectedUploadIds || selectedUploadIds.size === 0 ?
                  'Selecione uploads para excluir'
                : `Excluir ${selectedUploadIds.size} uploads selecionados`
              }
              style={{
                minWidth: 'auto',
                width: '24px',
                height: '24px',
                padding: 0,
                borderRadius: '4px',
              }}>
              <Button.Icon
                value={DeleteFive}
                color={
                  !selectedUploadIds || selectedUploadIds.size === 0 ? Tokens.ColorTextTertiary : Tokens.ColorUIDanger
                }
              />
            </Button.Root>
            <CancelButton $variant="link" onClick={handleCancelSelection}>
              <Button.Text value="Cancelar" />
            </CancelButton>
          </Flex>
        </Flex>
      }
    </Flex>
  )

  return (
    <Layout.Root>
      <Layout.Padder>
        <PageTitle
          title="Uploads"
          subtitle="Faça o upload de planilhas para a empresa"
          icon={UploadLogs}
          backToName={company?.name}
          onBackClick={() => navigate(Routes.CompanyDetails.path, companyID!)}
        />
        <Flex $type="column" $justify="flex-start" $align="stretch">
          <Uploader onUpload={handleUpload} requiredExtensions={['xlsx']} />
          <SelectionHeader />
          {(isLoadingUploads || isLoadingCompany) && !uploads?.elements.length ?
            <SkeletonLoader />
          : <>
              <CardsContainer>
                {uploadedItems.map((upload) => (
                  <Card
                    key={upload.id}
                    item={upload}
                    isSelectionModeActive={isSelectionModeActive}
                    isSelected={selectedUploadIds.has(`${upload.id}_${companyID}`)}
                    onToggleSelection={() => toggleUploadSelection(upload.id!, companyID!)}
                  />
                ))}
              </CardsContainer>
              <PaginationContainer>
                <Paginator totalPages={uploads?.pages} />
              </PaginationContainer>
            </>
          }
        </Flex>
      </Layout.Padder>
    </Layout.Root>
  )
}
